// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import {
  initializeFirestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Check if running in iOS WebView context
const isIOSWebView = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('source') === 'ios';
};

// Initialize Firebase Auth with appropriate persistence
// Use localStorage-based persistence for iOS WebView to avoid IndexedDB issues
// IndexedDB can hang in WKWebView, but localStorage works reliably
let auth;
try {
  if (isIOSWebView()) {
    console.log(
      '[Firebase] iOS WebView detected, using browserLocalPersistence (localStorage)'
    );
    auth = initializeAuth(app, {
      persistence: browserLocalPersistence,
    });
  } else {
    auth = getAuth(app);
  }
} catch (e) {
  // Auth may already be initialized, fall back to getAuth
  console.log('[Firebase] Auth already initialized, using getAuth');
  auth = getAuth(app);
}

export { auth };
// Force Firestore long polling. WKWebView (and apparently Safari talking
// to housfully-dev too — see the "Fetch API cannot load … Listen/channel
// … due to access control checks" errors) rejects the WebSocket upgrade
// to `firestore.googleapis.com/.../Listen/channel` for CORS reasons. The
// SDK's WebSocket-attempt timeout is ~30s and dominates every fresh
// page load.
//
// `experimentalAutoDetectLongPolling: true` did NOT help — the
// auto-detect probe also goes through the failing WebSocket path and
// itself takes ~30s before falling back. `experimentalForceLongPolling
// : true` skips WebSocket entirely so every connection starts on the
// reliable HTTP long-polling transport.
//
// Trade-off: long polling has slightly higher per-message latency than
// WebSocket on a healthy connection. For our usage (one-shot getDocs +
// occasional onSnapshot) it's negligible, and certainly worth losing
// the 30s tail.
//
// Must be called BEFORE any other Firestore code touches `app`. We're
// the first Firestore import in the dependency graph, so we're safe.
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
});
export const storage = getStorage(app);
// Not against the emulators: their traffic is not the product's, and with the
// share-link suite's emulator-only settings (no real API key, the nightly's
// clean tree) analytics' config fetch is refused and surfaced as an uncaught
// error over the whole page. Nothing reads this export.
export const analytics =
  process.env.REACT_APP_USE_EMULATORS === 'true' ? null : getAnalytics(app);
export const functions = getFunctions(app);

// Quick version check from browser console: window.checkVersion().then(r => console.log(r.data))
window.checkVersion = () => httpsCallable(functions, 'testEmulator')({});

// Functions emulator is OPT-IN (separate from the Firestore/Auth/Storage suite
// below). By default every callable hits real/deployed dev — features work with
// no emulator running. Opt in with `npm run start:dev:fns-emulator`
// (REACT_APP_USE_FUNCTIONS_EMULATOR) to develop cloud functions locally against
// real dev data, or via the full isolated suite (REACT_APP_USE_EMULATORS). Wired
// here, where `functions` is created, so the routing can't depend on which module
// happens to be imported first. Never connects in a production build.
/**
 * Host the emulators are reachable on, from wherever this page is being viewed.
 *
 * NOT hardcoded `localhost`: the iOS app loads this dev server over the LAN, so
 * on a PHYSICAL DEVICE `localhost` means the phone — the callable never leaves
 * the handset and fails before it can reach the Mac. (It works in the simulator,
 * which shares the host's loopback, which is what makes this easy to miss.)
 *
 * The page was served BY the machine running the emulators, so its own hostname
 * is the correct target in every case: `localhost` in a desktop browser and in
 * the simulator, the Mac's LAN IP on a device.
 */
const emulatorHost = () => window.location.hostname || 'localhost';

const useFunctionsEmulator =
  process.env.NODE_ENV === 'development' &&
  (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true' ||
    process.env.REACT_APP_USE_EMULATORS === 'true');
if (useFunctionsEmulator && !window._FUNCTIONS_EMULATOR_CONFIGURED) {
  // Per-worktree port; setup-worktree.sh writes REACT_APP_FUNCTIONS_EMULATOR_PORT
  // into .env.development.local (default 5001 = main worktree).
  const fnsPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT
    ? Number(process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT)
    : 5001;
  try {
    connectFunctionsEmulator(functions, emulatorHost(), fnsPort);
    window._FUNCTIONS_EMULATOR_CONFIGURED = true;
    console.log(`[Firebase] Functions emulator @ ${emulatorHost()}:${fnsPort}`);
  } catch (error) {
    console.error('Failed to connect to Functions emulator:', error);
  }
}

// Isolated-emulator mode (opt-in). When REACT_APP_USE_EMULATORS is set, point
// Firestore/Storage/Auth at this worktree's local emulator ports (offset-derived;
// written to .env.development.local by setup-worktree.sh, base defaults otherwise).
// Off by default -> shared real housfully-dev. (Functions are wired just above.)
// See claude-docs/2026-06-08-emulator-suite-flag-design.md.
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  const emuPort = (envVal, base) => (envVal ? Number(envVal) : base);
  const fsPort = emuPort(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT, 8080);
  const stPort = emuPort(process.env.REACT_APP_STORAGE_EMULATOR_PORT, 9199);
  const auPort = emuPort(process.env.REACT_APP_AUTH_EMULATOR_PORT, 9099);
  const host = emulatorHost();
  connectFirestoreEmulator(db, host, fsPort);
  connectStorageEmulator(storage, host, stPort);
  connectAuthEmulator(auth, `http://${host}:${auPort}`, {
    disableWarnings: true,
  });
  console.log(
    `[Firebase] USE_EMULATORS @ ${host} — Firestore:${fsPort} Storage:${stPort} Auth:${auPort}`
  );
}

export default app;
